import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Upload, Trash2, Euro, Image as ImageIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";

/**
 * GuessPriceEditor — embedded in AdminGameEditor when game.type === "guess_price".
 *
 * Lets the admin upload the product/window image, enter the secret real
 * price (never exposed to players), and set the winners_count for the
 * tie-breaker draw.
 *
 * Reuses the existing /api/uploads/game-background endpoint for image
 * uploads (same storage bucket, different field on the game doc).
 *
 * Props:
 *   game: full game object (real_price, price_image, winners_count, …)
 *   onGameChange(patch): persist top-level game field changes
 *   onReload(): refetch the game (unused here, kept for symmetry)
 */
export default function GuessPriceEditor({ game, onGameChange }) {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [stats, setStats] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!game?.id) return;
    api
      .get(`/games/${game.id}/guess-price/stats`)
      .then((r) => setStats(r.data))
      .catch(() => {});
  }, [game?.id, game?.real_price]);

  async function uploadImage(file) {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads/game-background", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onGameChange({ price_image: r.data.url });
      toast.success(t("common.save"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  return (
    <div className="qrise-card p-6 space-y-5" data-testid="guess-price-editor">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-medium inline-flex items-center gap-1">
            <Euro className="h-3 w-3" />
            {t("game_editor.gp_real_price")}
          </Label>
          <Input
            type="number"
            step="0.01"
            min={0}
            value={game.real_price ?? ""}
            onChange={(e) => {
              const v = e.target.value;
              onGameChange({
                real_price: v === "" ? null : Math.max(0, parseFloat(v)),
              });
            }}
            placeholder="0.00"
            className="mt-1.5 rounded-xl h-10 text-sm font-bold"
            data-testid="gp-real-price-input"
          />
          <p className="text-[11px] text-muted-foreground leading-snug mt-1.5 text-rose-500/80">
            🔒 {t("game_editor.gp_real_price_hint")}
          </p>
        </div>
        <div>
          <Label className="text-xs font-medium">
            {t("game_editor.winners_count")}
          </Label>
          <Input
            type="number"
            min={1}
            value={game.winners_count ?? 1}
            onChange={(e) =>
              onGameChange({
                winners_count: Math.max(1, parseInt(e.target.value) || 1),
              })
            }
            className="mt-1.5 rounded-xl h-10"
            data-testid="gp-winners-count-input"
          />
          <p className="text-[11px] text-muted-foreground leading-snug mt-1.5">
            {t("game_editor.winners_count_hint")}
          </p>
        </div>
      </div>

      <div>
        <Label className="text-xs font-medium inline-flex items-center gap-1">
          <ImageIcon className="h-3 w-3" />
          {t("game_editor.gp_image")}
        </Label>
        <p className="text-[11px] text-muted-foreground leading-snug mt-1 mb-2">
          {t("game_editor.gp_image_hint")}
        </p>
        {game.price_image ? (
          <div className="relative rounded-xl overflow-hidden border border-border bg-muted/20">
            <img
              src={game.price_image}
              alt="product"
              className="w-full h-56 object-cover"
              data-testid="gp-image-preview"
            />
            <button
              type="button"
              onClick={() => onGameChange({ price_image: null })}
              className="absolute top-2 right-2 h-8 w-8 rounded-full bg-background/95 text-destructive hover:text-destructive inline-flex items-center justify-center shadow-sm"
              data-testid="gp-image-remove"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <label className="block rounded-xl border-2 border-dashed border-border hover:border-foreground/40 hover:bg-muted/20 transition-colors cursor-pointer p-8 text-center">
            <Upload className="h-6 w-6 mx-auto text-muted-foreground mb-2" />
            <div className="text-sm font-medium">
              {uploading ? t("rules.generating") : t("game_editor.gp_image")}
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">
              PNG / JPEG · max 5 MB
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={(e) => uploadImage(e.target.files?.[0])}
              className="hidden"
              data-testid="gp-image-input"
              disabled={uploading}
            />
          </label>
        )}
      </div>

      {stats && (
        <div
          className="rounded-xl border border-border/60 bg-muted/20 p-3 text-xs space-y-1"
          data-testid="gp-stats"
        >
          <div className="flex justify-between">
            <span className="text-muted-foreground">Participants</span>
            <span className="font-semibold">{stats.total_participants}</span>
          </div>
          {stats.min_distance !== null && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Closest distance</span>
              <span className="font-semibold">
                {stats.min_distance.toFixed(2)} €
              </span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-muted-foreground">Tied at closest</span>
            <span className="font-semibold">{stats.eligible_count}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Winners drawn</span>
            <span className="font-semibold">{stats.winners_count}</span>
          </div>
        </div>
      )}
    </div>
  );
}
